import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ArrowLeft, RefreshCw, Search, TrendingUp, TrendingDown, Factory, Users, History, Filter, X, BarChart3, Calendar, ChevronDown, Check, Package } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Cell,
  LabelList,
  AreaChart,
  Area,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StoreReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Price History Data
  const [priceHistory, setPriceHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Filters
  const [selectedMill, setSelectedMill] = useState("");
  const [selectedDealer, setSelectedDealer] = useState("");
  const [selectedRmName, setSelectedRmName] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Dropdown open states
  const [millOpen, setMillOpen] = useState(false);
  const [dealerOpen, setDealerOpen] = useState(false);
  const [rmNameOpen, setRmNameOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  
  // Unique mills and dealers for filters
  const [mills, setMills] = useState([]);
  const [dealers, setDealers] = useState([]);
  
  // Selected material for chart
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // Multi-select for Price Comparison Table
  const [selectedMillsForTable, setSelectedMillsForTable] = useState([]);
  const [selectedDealersForTable, setSelectedDealersForTable] = useState([]);

  // Consumption Report Data
  const [consumptionItems, setConsumptionItems] = useState([]);
  const [consumptionHistory, setConsumptionHistory] = useState([]);
  const [consumptionDateFrom, setConsumptionDateFrom] = useState("");
  const [consumptionDateTo, setConsumptionDateTo] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Each call is independent — wrap each in its own try so one 403/500
      // doesn't abort the others. Previously a failure on /unique-mills-dealers
      // (Pro-gated) blocked fetchPriceHistory entirely on Core.
      try {
        const filtersRes = await axios.get(`${BACKEND_URL}/api/reports/unique-mills-dealers`);
        setMills(filtersRes.data.mills || []);
        setDealers(filtersRes.data.dealers || []);
      } catch (err) {
        console.warn("unique-mills-dealers not available:", err?.response?.status);
        setMills([]);
        setDealers([]);
      }

      try {
        await fetchPriceHistory();
      } catch (err) {
        console.warn("price-history fetch failed:", err?.response?.status);
      }

      // Fetch consumption data
      try {
        const [cItemsRes, cHistoryRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/consumption-inventory`),
          axios.get(`${BACKEND_URL}/api/consumption-stock-out/history`),
        ]);
        setConsumptionItems(cItemsRes.data);
        setConsumptionHistory(cHistoryRes.data);
      } catch (err) { /* consumption data optional */ }
    } catch (error) {
      toast.error("Failed to fetch report data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedMill) params.append("mill_name", selectedMill);
      if (selectedDealer) params.append("dealer_name", selectedDealer);
      
      const response = await axios.get(`${BACKEND_URL}/api/reports/price-history?${params.toString()}`);
      setPriceHistory(response.data.items || []);
    } catch (error) {
      console.error("Error fetching price history:", error);
      setPriceHistory([]);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchPriceHistory();
    }
  }, [selectedMill, selectedDealer]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Report data refreshed");
  };

  const clearFilters = () => {
    setSelectedMill("");
    setSelectedDealer("");
    setSelectedRmName("");
    setSelectedMonth("");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
  };

  // Get unique RM names
  const getUniqueRmNames = () => {
    const names = new Set();
    priceHistory.forEach(item => {
      if (item.rm_name) names.add(item.rm_name);
    });
    return Array.from(names).sort();
  };

  // Get unique months from price data
  const getAvailableMonths = () => {
    const months = new Set();
    priceHistory.forEach(item => {
      item.prices?.forEach(p => {
        if (p.date) {
          const date = new Date(p.date);
          const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          months.add(monthYear);
        }
      });
    });
    return Array.from(months).sort().reverse();
  };

  // Filter price history
  const filteredPriceHistory = priceHistory.filter(item => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!item.rm_name?.toLowerCase().includes(term) &&
          !item.product_type?.toLowerCase().includes(term) &&
          !item.mill_name?.toLowerCase().includes(term) &&
          !item.dealer_name?.toLowerCase().includes(term)) {
        return false;
      }
    }
    // RM Name filter
    if (selectedRmName && item.rm_name !== selectedRmName) {
      return false;
    }
    return true;
  }).map(item => {
    // Filter prices by date/month
    let filteredPrices = item.prices || [];
    
    if (selectedMonth && selectedMonth !== "") {
      filteredPrices = filteredPrices.filter(p => {
        if (!p.date) return false;
        const date = new Date(p.date);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return monthYear === selectedMonth;
      });
    }
    
    if (startDate) {
      const start = new Date(startDate);
      filteredPrices = filteredPrices.filter(p => new Date(p.date) >= start);
    }
    
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filteredPrices = filteredPrices.filter(p => new Date(p.date) <= end);
    }
    
    return { ...item, prices: filteredPrices };
  }).filter(item => item.prices.length > 0);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (err) {
      return dateStr;
    }
  };

  // Format month for display
  const formatMonth = (monthYear) => {
    if (!monthYear) return "";
    const [year, month] = monthYear.split("-");
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long' });
  };

  // Calculate price trend
  const getPriceTrend = (prices) => {
    if (!prices || prices.length < 2) return null;
    const latest = prices[0]?.price || 0;
    const previous = prices[1]?.price || 0;
    if (latest > previous) return { direction: "up", diff: (latest - previous).toFixed(2) };
    if (latest < previous) return { direction: "down", diff: (previous - latest).toFixed(2) };
    return { direction: "same", diff: "0.00" };
  };

  // Prepare chart data for selected material - Column chart with prices over time
  const getColumnChartData = (material) => {
    if (!material || !material.prices || material.prices.length === 0) return [];
    
    // Reverse to show oldest to newest and format for column chart
    return [...material.prices].reverse().map(p => ({
      date: formatDate(p.date),
      shortDate: new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      price: p.price || 0,
      reel_id: p.reel_id || ""
    }));
  };

  // Get all prices for comparison chart - NEW: Horizontal bar chart data
  const getComparisonChartData = () => {
    // Get latest price for each material with change info
    return filteredPriceHistory.slice(0, 8).map(item => {
      const latestPrice = item.prices[0]?.price || 0;
      const previousPrice = item.prices[1]?.price || latestPrice;
      const priceChange = latestPrice - previousPrice;
      const percentChange = previousPrice > 0 ? ((priceChange / previousPrice) * 100) : 0;
      
      return {
        name: item.rm_name,
        shortName: item.rm_name.length > 12 ? item.rm_name.substring(0, 12) + '...' : item.rm_name,
        price: latestPrice,
        previousPrice: previousPrice,
        change: priceChange,
        percentChange: percentChange,
        isUp: priceChange > 0,
        isDown: priceChange < 0,
        mill: item.mill_name || 'N/A',
        dealer: item.dealer_name || 'N/A'
      };
    }).sort((a, b) => b.price - a.price); // Sort by price descending
  };

  // Get data for Price Comparison Tables (grouped by Mill/Dealer)
  const getPriceComparisonTableData = () => {
    const tables = [];
    
    // Get unique months from all price history (last 6 months)
    const allMonths = new Set();
    filteredPriceHistory.forEach(item => {
      item.prices.forEach(p => {
        const date = new Date(p.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        allMonths.add(monthKey);
      });
    });
    const sortedMonths = Array.from(allMonths).sort().reverse().slice(0, 6);
    
    // Filter by selected mills and dealers
    const selectedMills = selectedMillsForTable.length > 0 ? selectedMillsForTable : mills.slice(0, 3);
    const selectedDealers = selectedDealersForTable.length > 0 ? selectedDealersForTable : [];
    
    // Group data by mill
    selectedMills.forEach(mill => {
      const millData = filteredPriceHistory.filter(item => item.mill_name === mill);
      if (millData.length === 0) return;
      
      const rows = millData.map(item => {
        const row = {
          rm_name: item.rm_name,
          dealer: item.dealer_name || 'N/A',
        };
        
        // Add price for each month
        sortedMonths.forEach(month => {
          const priceForMonth = item.prices.find(p => {
            const pDate = new Date(p.date);
            const pMonth = `${pDate.getFullYear()}-${String(pDate.getMonth() + 1).padStart(2, '0')}`;
            return pMonth === month;
          });
          row[month] = priceForMonth?.price || null;
        });
        
        // Calculate trend
        const prices = sortedMonths.map(m => row[m]).filter(p => p !== null);
        if (prices.length >= 2) {
          const latestPrice = prices[0];
          const oldestPrice = prices[prices.length - 1];
          row.trend = latestPrice - oldestPrice;
          row.trendPercent = ((latestPrice - oldestPrice) / oldestPrice * 100);
        } else {
          row.trend = 0;
          row.trendPercent = 0;
        }
        
        return row;
      });
      
      tables.push({
        type: 'mill',
        name: mill,
        months: sortedMonths,
        rows: rows
      });
    });
    
    // Group data by dealer
    selectedDealers.forEach(dealer => {
      const dealerData = filteredPriceHistory.filter(item => item.dealer_name === dealer);
      if (dealerData.length === 0) return;
      
      const rows = dealerData.map(item => {
        const row = {
          rm_name: item.rm_name,
          mill: item.mill_name || 'N/A',
        };
        
        // Add price for each month
        sortedMonths.forEach(month => {
          const priceForMonth = item.prices.find(p => {
            const pDate = new Date(p.date);
            const pMonth = `${pDate.getFullYear()}-${String(pDate.getMonth() + 1).padStart(2, '0')}`;
            return pMonth === month;
          });
          row[month] = priceForMonth?.price || null;
        });
        
        // Calculate trend
        const prices = sortedMonths.map(m => row[m]).filter(p => p !== null);
        if (prices.length >= 2) {
          const latestPrice = prices[0];
          const oldestPrice = prices[prices.length - 1];
          row.trend = latestPrice - oldestPrice;
          row.trendPercent = ((latestPrice - oldestPrice) / oldestPrice * 100);
        } else {
          row.trend = 0;
          row.trendPercent = 0;
        }
        
        return row;
      });
      
      tables.push({
        type: 'dealer',
        name: dealer,
        months: sortedMonths,
        rows: rows
      });
    });
    
    return tables;
  };

  // Format month for table display (short format)
  const formatMonthShort = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Toggle mill selection
  const toggleMillSelection = (mill) => {
    setSelectedMillsForTable(prev => 
      prev.includes(mill) 
        ? prev.filter(m => m !== mill)
        : [...prev, mill]
    );
  };

  // Toggle dealer selection
  const toggleDealerSelection = (dealer) => {
    setSelectedDealersForTable(prev => 
      prev.includes(dealer) 
        ? prev.filter(d => d !== dealer)
        : [...prev, dealer]
    );
  };

  const hasFilters = selectedMill || selectedDealer || selectedRmName || selectedMonth || startDate || endDate || searchTerm;
  const availableMonths = getAvailableMonths();
  const uniqueRmNames = getUniqueRmNames();

  // Colors for different materials in comparison chart
  const chartColors = ['#06B6D4', '#A855F7', '#F59E0B', '#10B981', '#EF4444', '#EC4899', '#8B5CF6', '#14B8A6'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/store/dashboard")}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-cyan-400 tracking-wider flex items-center gap-3">
              <BarChart3 className="h-8 w-8" />
              Price History Report
            </h1>
            <p className="text-gray-400 mt-1">Track paper prices over time</p>
          </div>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
          disabled={refreshing}
          data-testid="refresh-reports-btn"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-black/60 border-2 border-cyan-500/30">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-cyan-400 flex items-center gap-2 text-lg">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-400 hover:text-white">
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
            {/* RM Name - Searchable Dropdown */}
            <div>
              <Label className="text-cyan-300 text-sm flex items-center gap-2">
                <Package className="h-4 w-4" />
                RM Name
              </Label>
              <Popover open={rmNameOpen} onOpenChange={setRmNameOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={rmNameOpen} className="w-full justify-between mt-1 bg-black/50 border-cyan-500/30 text-white hover:bg-cyan-500/10">
                    <span className="truncate">{selectedRmName || "All Materials"}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-cyan-500/30" align="start">
                  <Command className="bg-transparent">
                    <CommandInput placeholder="Search RM name..." className="text-white" />
                    <CommandList>
                      <CommandEmpty className="text-gray-400 py-2 text-center text-sm">No material found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setSelectedRmName(""); setRmNameOpen(false); }} className="text-gray-400 hover:bg-cyan-500/20">
                          <Check className={cn("mr-2 h-4 w-4", !selectedRmName ? "opacity-100" : "opacity-0")} />
                          All Materials
                        </CommandItem>
                        {uniqueRmNames.map((name) => (
                          <CommandItem key={name} onSelect={() => { setSelectedRmName(name); setRmNameOpen(false); }} className="text-white hover:bg-cyan-500/20">
                            <Check className={cn("mr-2 h-4 w-4", selectedRmName === name ? "opacity-100" : "opacity-0")} />
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Mill Name - Searchable Dropdown */}
            <div>
              <Label className="text-cyan-300 text-sm flex items-center gap-2">
                <Factory className="h-4 w-4" />
                Mill Name
              </Label>
              <Popover open={millOpen} onOpenChange={setMillOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={millOpen} className="w-full justify-between mt-1 bg-black/50 border-cyan-500/30 text-white hover:bg-cyan-500/10">
                    <span className="truncate">{selectedMill || "All Mills"}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-cyan-500/30" align="start">
                  <Command className="bg-transparent">
                    <CommandInput placeholder="Search mill..." className="text-white" />
                    <CommandList>
                      <CommandEmpty className="text-gray-400 py-2 text-center text-sm">No mill found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setSelectedMill(""); setMillOpen(false); }} className="text-gray-400 hover:bg-cyan-500/20">
                          <Check className={cn("mr-2 h-4 w-4", !selectedMill ? "opacity-100" : "opacity-0")} />
                          All Mills
                        </CommandItem>
                        {mills.map((mill) => (
                          <CommandItem key={mill} onSelect={() => { setSelectedMill(mill); setMillOpen(false); }} className="text-white hover:bg-cyan-500/20">
                            <Check className={cn("mr-2 h-4 w-4", selectedMill === mill ? "opacity-100" : "opacity-0")} />
                            {mill}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Dealer Name - Searchable Dropdown */}
            <div>
              <Label className="text-cyan-300 text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Dealer Name
              </Label>
              <Popover open={dealerOpen} onOpenChange={setDealerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={dealerOpen} className="w-full justify-between mt-1 bg-black/50 border-cyan-500/30 text-white hover:bg-cyan-500/10">
                    <span className="truncate">{selectedDealer || "All Dealers"}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-cyan-500/30" align="start">
                  <Command className="bg-transparent">
                    <CommandInput placeholder="Search dealer..." className="text-white" />
                    <CommandList>
                      <CommandEmpty className="text-gray-400 py-2 text-center text-sm">No dealer found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setSelectedDealer(""); setDealerOpen(false); }} className="text-gray-400 hover:bg-cyan-500/20">
                          <Check className={cn("mr-2 h-4 w-4", !selectedDealer ? "opacity-100" : "opacity-0")} />
                          All Dealers
                        </CommandItem>
                        {dealers.map((dealer) => (
                          <CommandItem key={dealer} onSelect={() => { setSelectedDealer(dealer); setDealerOpen(false); }} className="text-white hover:bg-cyan-500/20">
                            <Check className={cn("mr-2 h-4 w-4", selectedDealer === dealer ? "opacity-100" : "opacity-0")} />
                            {dealer}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Month - Searchable Dropdown */}
            <div>
              <Label className="text-cyan-300 text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Month
              </Label>
              <Popover open={monthOpen} onOpenChange={setMonthOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={monthOpen} className="w-full justify-between mt-1 bg-black/50 border-cyan-500/30 text-white hover:bg-cyan-500/10">
                    <span className="truncate">{selectedMonth ? formatMonth(selectedMonth) : "All Months"}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-cyan-500/30" align="start">
                  <Command className="bg-transparent">
                    <CommandInput placeholder="Search month..." className="text-white" />
                    <CommandList>
                      <CommandEmpty className="text-gray-400 py-2 text-center text-sm">No month found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setSelectedMonth(""); setMonthOpen(false); }} className="text-gray-400 hover:bg-cyan-500/20">
                          <Check className={cn("mr-2 h-4 w-4", !selectedMonth ? "opacity-100" : "opacity-0")} />
                          All Months
                        </CommandItem>
                        {availableMonths.map((month) => (
                          <CommandItem key={month} onSelect={() => { setSelectedMonth(month); setMonthOpen(false); }} className="text-white hover:bg-cyan-500/20">
                            <Check className={cn("mr-2 h-4 w-4", selectedMonth === month ? "opacity-100" : "opacity-0")} />
                            {formatMonth(month)}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Range */}
            <div>
              <Label className="text-cyan-300 text-sm">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 bg-black/50 border-cyan-500/30 text-white [color-scheme:dark]" />
            </div>
            <div>
              <Label className="text-cyan-300 text-sm">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 bg-black/50 border-cyan-500/30 text-white [color-scheme:dark]" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Price Comparison Chart - Horizontal Bar Chart (Redesigned) */}
      <Card className="bg-black/60 border-2 border-cyan-500/50">
        <CardHeader>
          <CardTitle className="text-cyan-400 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Price Comparison
            <span className="ml-2 text-sm text-gray-400 font-normal">
              Latest prices compared (sorted high to low)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPriceHistory.length > 0 ? (
            <div className="space-y-6">
              {/* Horizontal Bar Chart */}
              <div className="h-[320px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={getComparisonChartData()} 
                    layout="vertical"
                    margin={{ top: 10, right: 80, left: 100, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={true} vertical={false} />
                    <XAxis 
                      type="number"
                      stroke="#9CA3AF" 
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      tickFormatter={(val) => `₹${val}`}
                      domain={[0, 'dataMax + 10']}
                    />
                    <YAxis 
                      type="category"
                      dataKey="shortName" 
                      stroke="#9CA3AF" 
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      width={90}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #06B6D4', borderRadius: '8px' }}
                      labelStyle={{ color: '#06B6D4', fontWeight: 'bold' }}
                      formatter={(value, name, props) => {
                        const data = props.payload;
                        return [
                          <div key="tooltip" className="space-y-1">
                            <div className="text-white font-bold">₹{value?.toFixed(2)}/kg</div>
                            <div className="text-gray-400 text-xs">Previous: ₹{data.previousPrice?.toFixed(2)}</div>
                            <div className={cn(
                              "text-xs",
                              data.change > 0 ? "text-red-400" : data.change < 0 ? "text-green-400" : "text-gray-400"
                            )}>
                              Change: {data.change > 0 ? '+' : ''}{data.change?.toFixed(2)} ({data.percentChange?.toFixed(1)}%)
                            </div>
                          </div>,
                          ''
                        ];
                      }}
                      labelFormatter={(label, payload) => payload[0]?.payload?.name || label}
                    />
                    <Bar 
                      dataKey="price" 
                      radius={[0, 4, 4, 0]}
                      name="Current Price"
                    >
                      {getComparisonChartData().map((entry, index) => (
                        <Cell 
                          key={`cell-${entry.name || index}`} 
                          fill={chartColors[index % chartColors.length]}
                        />
                      ))}
                      <LabelList 
                        dataKey="price" 
                        position="right" 
                        formatter={(val) => `₹${val?.toFixed(0)}`}
                        fill="#9CA3AF"
                        fontSize={11}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Legend / Key */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-400 mb-3">Price Change Indicators</h4>
                <div className="flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-red-400">Price Increased</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-green-400">Price Decreased</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                    <span className="text-gray-400">No Change</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-500" />
              <p className="text-lg">No price data available</p>
              <p className="text-sm mt-2">Add inventory items with prices to see the chart</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Price Comparison Tables - Multi-select Mills & Dealers */}
      <Card className="bg-black/60 border-2 border-emerald-500/50">
        <CardHeader>
          <CardTitle className="text-emerald-400 flex items-center gap-2">
            <History className="h-5 w-5" />
            Price Comparison Tables
            <span className="ml-2 text-sm text-gray-400 font-normal">
              Select mills & dealers to compare
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Multi-select Bubbles */}
          <div className="space-y-4 mb-6">
            {/* Mill Selection */}
            <div>
              <Label className="text-emerald-300 text-sm flex items-center gap-2 mb-2">
                <Factory className="h-4 w-4" />
                Select Mills
              </Label>
              <div className="flex flex-wrap gap-2">
                {mills.map(mill => (
                  <button
                    key={mill}
                    onClick={() => toggleMillSelection(mill)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
                      "border hover:scale-105",
                      selectedMillsForTable.includes(mill)
                        ? "bg-emerald-500/30 border-emerald-400 text-emerald-300 shadow-lg shadow-emerald-500/20"
                        : "bg-gray-800/50 border-gray-600 text-gray-400 hover:border-emerald-500/50 hover:text-emerald-400"
                    )}
                  >
                    {selectedMillsForTable.includes(mill) && (
                      <Check className="h-3 w-3 inline mr-1" />
                    )}
                    {mill}
                  </button>
                ))}
                {mills.length === 0 && (
                  <span className="text-gray-500 text-sm">No mills available</span>
                )}
              </div>
            </div>

            {/* Dealer Selection */}
            <div>
              <Label className="text-cyan-300 text-sm flex items-center gap-2 mb-2">
                <Users className="h-4 w-4" />
                Select Dealers
              </Label>
              <div className="flex flex-wrap gap-2">
                {dealers.map(dealer => (
                  <button
                    key={dealer}
                    onClick={() => toggleDealerSelection(dealer)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
                      "border hover:scale-105",
                      selectedDealersForTable.includes(dealer)
                        ? "bg-cyan-500/30 border-cyan-400 text-cyan-300 shadow-lg shadow-cyan-500/20"
                        : "bg-gray-800/50 border-gray-600 text-gray-400 hover:border-cyan-500/50 hover:text-cyan-400"
                    )}
                  >
                    {selectedDealersForTable.includes(dealer) && (
                      <Check className="h-3 w-3 inline mr-1" />
                    )}
                    {dealer}
                  </button>
                ))}
                {dealers.length === 0 && (
                  <span className="text-gray-500 text-sm">No dealers available</span>
                )}
              </div>
            </div>

            {/* Selection Summary */}
            {(selectedMillsForTable.length > 0 || selectedDealersForTable.length > 0) && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>Selected:</span>
                {selectedMillsForTable.length > 0 && (
                  <span className="text-emerald-400">{selectedMillsForTable.length} mill(s)</span>
                )}
                {selectedMillsForTable.length > 0 && selectedDealersForTable.length > 0 && <span>•</span>}
                {selectedDealersForTable.length > 0 && (
                  <span className="text-cyan-400">{selectedDealersForTable.length} dealer(s)</span>
                )}
                <button 
                  onClick={() => { setSelectedMillsForTable([]); setSelectedDealersForTable([]); }}
                  className="ml-2 text-red-400 hover:text-red-300"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Comparison Tables */}
          {getPriceComparisonTableData().length > 0 ? (
            <div className="space-y-6">
              {getPriceComparisonTableData().map((tableData, tableIdx) => (
                <div key={`${tableData.type}-${tableData.name}`} className="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
                  {/* Table Header */}
                  <div className={cn(
                    "px-4 py-3 border-b border-gray-700 flex items-center gap-3",
                    tableData.type === 'mill' ? "bg-emerald-500/10" : "bg-cyan-500/10"
                  )}>
                    {tableData.type === 'mill' ? (
                      <Factory className="h-5 w-5 text-emerald-400" />
                    ) : (
                      <Users className="h-5 w-5 text-cyan-400" />
                    )}
                    <h3 className={cn(
                      "font-bold text-lg",
                      tableData.type === 'mill' ? "text-emerald-400" : "text-cyan-400"
                    )}>
                      {tableData.name}
                    </h3>
                    <span className="text-gray-500 text-sm ml-auto">
                      {tableData.rows.length} material(s)
                    </span>
                  </div>

                  {/* Table Content */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-gray-700 bg-gray-800/50">
                          <TableHead className="text-gray-300 font-semibold sticky left-0 bg-gray-800/90 z-10">RM Name</TableHead>
                          <TableHead className="text-gray-300 font-semibold">
                            {tableData.type === 'mill' ? 'Dealer' : 'Mill'}
                          </TableHead>
                          {tableData.months.map(month => (
                            <TableHead key={month} className="text-gray-300 font-semibold text-center min-w-[80px]">
                              {formatMonthShort(month)}
                            </TableHead>
                          ))}
                          <TableHead className="text-gray-300 font-semibold text-center">Trend</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tableData.rows.map((row, rowIdx) => (
                          <TableRow key={row.rm_name} className="border-gray-700/50 hover:bg-gray-800/30">
                            <TableCell className="font-medium text-white sticky left-0 bg-gray-900/90 z-10">
                              {row.rm_name}
                            </TableCell>
                            <TableCell className="text-gray-400 text-sm">
                              {tableData.type === 'mill' ? row.dealer : row.mill}
                            </TableCell>
                            {tableData.months.map(month => (
                              <TableCell key={month} className="text-center">
                                {row[month] !== null ? (
                                  <span className="text-white font-mono">₹{row[month].toFixed(0)}</span>
                                ) : (
                                  <span className="text-gray-600">-</span>
                                )}
                              </TableCell>
                            ))}
                            <TableCell className="text-center">
                              <div className={cn(
                                "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold",
                                row.trend > 0 ? "bg-red-500/20 text-red-400" :
                                row.trend < 0 ? "bg-green-500/20 text-green-400" :
                                "bg-gray-500/20 text-gray-400"
                              )}>
                                {row.trend > 0 ? <TrendingUp className="h-3 w-3" /> : 
                                 row.trend < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                                {row.trend !== 0 ? (
                                  <span>{row.trend > 0 ? '+' : ''}{row.trendPercent.toFixed(1)}%</span>
                                ) : (
                                  <span>-</span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <History className="h-12 w-12 mx-auto mb-3 text-gray-600" />
              <p>Select mills or dealers above to see comparison tables</p>
              <p className="text-sm text-gray-500 mt-1">Tables will show RM names with prices across months</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual Material Chart - Select and View */}
      <Card className="bg-black/60 border border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-300 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-gray-500" />
            Individual Material Price Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Material selector */}
          <div className="mb-6">
            <Label className="text-gray-400 mb-2 block">Select Raw Material</Label>
            <Select 
              value={selectedMaterial?.rm_name || ""} 
              onValueChange={(val) => {
                const mat = filteredPriceHistory.find(p => p.rm_name === val);
                setSelectedMaterial(mat);
              }}
            >
              <SelectTrigger className="bg-gray-900/50 border-gray-600 text-white max-w-md">
                <SelectValue placeholder="Choose a material to view detailed price trend" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-600 max-h-60">
                {filteredPriceHistory.map((item) => (
                  <SelectItem key={item.rm_name} value={item.rm_name}>
                    {item.rm_name} ({item.prices.length} records)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedMaterial ? (
            <div>
              {/* Material Info - Minimal Style */}
              <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{selectedMaterial.rm_name}</h3>
                    <div className="flex flex-wrap gap-3 text-sm mt-1">
                      {selectedMaterial.mill_name && (
                        <span className="text-gray-500">Mill: <span className="text-gray-400">{selectedMaterial.mill_name}</span></span>
                      )}
                      {selectedMaterial.dealer_name && (
                        <span className="text-gray-500">Dealer: <span className="text-gray-400">{selectedMaterial.dealer_name}</span></span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">₹{selectedMaterial.prices[0]?.price?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-gray-500">Current Price</p>
                  </div>
                </div>
              </div>
              
              {/* Area Chart - Low Profile */}
              {getColumnChartData(selectedMaterial).length > 0 ? (
                <div className="h-[200px] w-full mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={getColumnChartData(selectedMaterial).reverse()} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                      <defs>
                        <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4B5563" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#4B5563" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
                      <XAxis 
                        dataKey="shortDate" 
                        stroke="#4B5563" 
                        tick={{ fill: '#6B7280', fontSize: 10 }}
                        axisLine={{ stroke: '#374151' }}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#4B5563" 
                        tick={{ fill: '#6B7280', fontSize: 10 }}
                        tickFormatter={(val) => `₹${val}`}
                        domain={['dataMin - 5', 'dataMax + 5']}
                        axisLine={false}
                        tickLine={false}
                        width={50}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1F2937', 
                          border: '1px solid #374151', 
                          borderRadius: '6px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                        }}
                        labelStyle={{ color: '#9CA3AF', fontSize: '12px' }}
                        formatter={(value) => [`₹${value?.toFixed(2)}`, 'Price']}
                        labelFormatter={(label, payload) => payload[0]?.payload?.date || label}
                      />
                      <Area 
                        type="monotone"
                        dataKey="price" 
                        stroke="#6B7280"
                        strokeWidth={2}
                        fill="url(#priceGradient)"
                        name="Price (₹/kg)"
                        dot={{ fill: '#9CA3AF', strokeWidth: 0, r: 3 }}
                        activeDot={{ fill: '#F3F4F6', strokeWidth: 0, r: 5 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
              
              {/* Price History Table - Minimal */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">Price History</h4>
                <div className="overflow-x-auto max-h-[250px] rounded-lg border border-gray-800">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-800 bg-gray-900/50">
                        <TableHead className="text-gray-500 text-xs font-medium">Date</TableHead>
                        <TableHead className="text-gray-500 text-xs font-medium">Reel ID</TableHead>
                        <TableHead className="text-gray-500 text-xs font-medium text-right">Price</TableHead>
                        <TableHead className="text-gray-500 text-xs font-medium text-center">Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedMaterial.prices.map((p, idx) => {
                        const prevPrice = selectedMaterial.prices[idx + 1]?.price || 0;
                        const priceDiff = p.price - prevPrice;
                        const isFirst = idx === 0;
                        
                        return (
                          <TableRow key={`price-${p.date}-${p.reel_id || idx}`} className={cn("border-gray-800/50", isFirst && "bg-gray-800/30")}>
                            <TableCell className={cn("text-sm", isFirst ? "text-white" : "text-gray-400")}>
                              {formatDate(p.date)}
                              {isFirst && <span className="ml-2 text-[10px] bg-gray-700 px-1.5 py-0.5 rounded text-gray-300">Latest</span>}
                            </TableCell>
                            <TableCell className="text-gray-500 font-mono text-xs">{p.reel_id || "-"}</TableCell>
                            <TableCell className={cn("text-right font-medium", isFirst ? "text-white" : "text-gray-300")}>
                              ₹{p.price?.toFixed(2) || "0.00"}
                            </TableCell>
                            <TableCell className="text-center">
                              {idx < selectedMaterial.prices.length - 1 && (
                                <span className={cn(
                                  "px-1.5 py-0.5 rounded text-[10px] font-medium",
                                  priceDiff > 0 && "bg-red-500/10 text-red-400/80",
                                  priceDiff < 0 && "bg-green-500/10 text-green-400/80",
                                  priceDiff === 0 && "bg-gray-500/10 text-gray-500"
                                )}>
                                  {priceDiff > 0 ? "+" : ""}{priceDiff.toFixed(2)}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-10">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-700" />
              <p className="text-gray-500">Select a material from the dropdown above</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Price History Summary Table */}
      <Card className="bg-black/60 border-2 border-cyan-500/50">
        <CardHeader>
          <CardTitle className="text-cyan-400 flex items-center gap-2">
            <History className="h-5 w-5" />
            Price History Summary
            <span className="ml-2 px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">
              {filteredPriceHistory.length} materials
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading price history...</div>
          ) : filteredPriceHistory.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-16 w-16 mx-auto mb-4 text-gray-500" />
              <p className="text-xl font-bold text-gray-400 mb-2">No Price History Found</p>
              <p className="text-gray-500">Add inventory items with prices to see history.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-cyan-500/20 hover:bg-transparent">
                    <TableHead className="text-cyan-300">RM Name</TableHead>
                    <TableHead className="text-cyan-300">Type</TableHead>
                    <TableHead className="text-cyan-300">Mill</TableHead>
                    <TableHead className="text-cyan-300">Dealer</TableHead>
                    <TableHead className="text-cyan-300 text-center">Records</TableHead>
                    <TableHead className="text-cyan-300 text-right">Latest Price</TableHead>
                    <TableHead className="text-cyan-300 text-center">Trend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPriceHistory.map((item, idx) => {
                    const trend = getPriceTrend(item.prices);
                    const latestPrice = item.prices[0]?.price || 0;
                    
                    return (
                      <TableRow 
                        key={idx} 
                        className="border-cyan-500/10 hover:bg-cyan-500/5 cursor-pointer"
                        onClick={() => setSelectedMaterial(item)}
                      >
                        <TableCell className="font-medium text-white">{item.rm_name}</TableCell>
                        <TableCell className="text-gray-400">{item.product_type || "-"}</TableCell>
                        <TableCell className="text-cyan-400">{item.mill_name || "-"}</TableCell>
                        <TableCell className="text-cyan-400">{item.dealer_name || "-"}</TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm">
                            {item.prices.length}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-bold text-white">₹{latestPrice.toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          {trend && (
                            <span className={cn(
                              "px-2 py-1 rounded text-xs font-bold inline-flex items-center gap-1",
                              trend.direction === "up" && "bg-red-500/20 text-red-400",
                              trend.direction === "down" && "bg-green-500/20 text-green-400",
                              trend.direction === "same" && "bg-gray-500/20 text-gray-400"
                            )}>
                              {trend.direction === "up" && <TrendingUp className="h-3 w-3" />}
                              {trend.direction === "down" && <TrendingDown className="h-3 w-3" />}
                              {trend.direction === "up" ? "+" : trend.direction === "down" ? "-" : ""}₹{trend.diff}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consumption Inventory Report */}
      {consumptionItems.length > 0 && (
        <Card className="bg-black/60 border-2 border-emerald-500/30 mt-6" data-testid="consumption-report-section">
          <CardHeader>
            <CardTitle className="text-xl text-emerald-400 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Consumption Inventory Report
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-center">
                <p className="text-xl font-black text-emerald-400">{consumptionItems.length}</p>
                <p className="text-xs text-emerald-300/70">Total Items</p>
              </div>
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-center">
                <p className="text-xl font-black text-cyan-400">
                  ₹{consumptionItems.reduce((s, i) => s + ((i.total_qty || 0) * (i.unit_price || 0)), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </p>
                <p className="text-xs text-cyan-300/70">Total Value</p>
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                <p className="text-xl font-black text-green-400">
                  ₹{consumptionItems.reduce((s, i) => s + ((i.available_qty || 0) * (i.unit_price || 0)), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </p>
                <p className="text-xs text-green-300/70">Available Value</p>
              </div>
              <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg text-center">
                <p className="text-xl font-black text-orange-400">
                  ₹{consumptionItems.reduce((s, i) => s + ((i.used_qty || 0) * (i.unit_price || 0)), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </p>
                <p className="text-xs text-orange-300/70">Consumed Value</p>
              </div>
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                <p className="text-xl font-black text-red-400">{consumptionItems.filter(i => i.available_qty <= 0).length}</p>
                <p className="text-xs text-red-300/70">Out of Stock</p>
              </div>
            </div>

            {/* Date filter for history */}
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-emerald-300 text-sm">Filter History:</Label>
              <Input type="date" value={consumptionDateFrom} onChange={e => setConsumptionDateFrom(e.target.value)} className="bg-gray-900 border-gray-600 text-white w-40" />
              <span className="text-gray-500">to</span>
              <Input type="date" value={consumptionDateTo} onChange={e => setConsumptionDateTo(e.target.value)} className="bg-gray-900 border-gray-600 text-white w-40" />
              {(consumptionDateFrom || consumptionDateTo) && (
                <Button size="sm" variant="outline" onClick={() => { setConsumptionDateFrom(""); setConsumptionDateTo(""); }} className="border-red-500/30 text-red-400">
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              )}
            </div>

            {/* Stock Out History Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-emerald-400 font-bold">Date</TableHead>
                    <TableHead className="text-emerald-400 font-bold">Category</TableHead>
                    <TableHead className="text-emerald-400 font-bold">Item</TableHead>
                    <TableHead className="text-emerald-400 font-bold text-center">Qty Out</TableHead>
                    <TableHead className="text-emerald-400 font-bold text-right">Value</TableHead>
                    <TableHead className="text-emerald-400 font-bold">Taken By</TableHead>
                    <TableHead className="text-emerald-400 font-bold">Purpose</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    let filtered = consumptionHistory;
                    if (consumptionDateFrom) filtered = filtered.filter(h => h.timestamp >= consumptionDateFrom);
                    if (consumptionDateTo) filtered = filtered.filter(h => h.timestamp <= consumptionDateTo + "T23:59:59");
                    return filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-500">No stock-out records found</TableCell></TableRow>
                    ) : filtered.map(log => {
                      const item = consumptionItems.find(i => i.id === log.item_id);
                      const value = (log.quantity || 0) * (item?.unit_price || 0);
                      return (
                        <TableRow key={log.id} className="border-gray-800">
                          <TableCell className="text-gray-400 text-xs">{new Date(log.timestamp).toLocaleDateString("en-IN")}</TableCell>
                          <TableCell className="text-gray-400 text-sm">{log.category}</TableCell>
                          <TableCell className="text-white text-sm">{log.item_name}</TableCell>
                          <TableCell className="text-center text-orange-400 font-bold">{log.quantity} {log.unit}</TableCell>
                          <TableCell className="text-right text-gray-300">{value > 0 ? `₹${value.toLocaleString()}` : "—"}</TableCell>
                          <TableCell className="text-gray-300 text-sm">{log.person_name}</TableCell>
                          <TableCell className="text-gray-400 text-sm">{log.purpose || "—"}</TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </div>

            {/* Category Consumption Summary */}
            <div className="mt-4">
              <h3 className="text-sm font-bold text-emerald-300 mb-2">Category-wise Consumption Summary</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="py-2 text-emerald-400 text-xs font-bold">Category</th>
                      <th className="py-2 text-emerald-400 text-xs font-bold text-center">Items</th>
                      <th className="py-2 text-emerald-400 text-xs font-bold text-center">Total Qty</th>
                      <th className="py-2 text-emerald-400 text-xs font-bold text-center">Available</th>
                      <th className="py-2 text-emerald-400 text-xs font-bold text-center">Used</th>
                      <th className="py-2 text-emerald-400 text-xs font-bold text-right">Usage %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const catMap = {};
                      consumptionItems.forEach(i => {
                        const cat = i.category || "Other";
                        if (!catMap[cat]) catMap[cat] = { count: 0, total: 0, available: 0, used: 0 };
                        catMap[cat].count++;
                        catMap[cat].total += i.total_qty || 0;
                        catMap[cat].available += i.available_qty || 0;
                        catMap[cat].used += i.used_qty || 0;
                      });
                      return Object.entries(catMap).map(([cat, data]) => {
                        const usagePct = data.total > 0 ? ((data.used / data.total) * 100).toFixed(1) : "0";
                        return (
                          <tr key={cat} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="py-2 text-white text-sm">{cat}</td>
                            <td className="py-2 text-center text-gray-400">{data.count}</td>
                            <td className="py-2 text-center text-gray-300">{data.total.toLocaleString()}</td>
                            <td className="py-2 text-center text-emerald-400 font-bold">{data.available.toLocaleString()}</td>
                            <td className="py-2 text-center text-orange-400">{data.used.toLocaleString()}</td>
                            <td className="py-2 text-right">
                              <span className={cn("px-2 py-0.5 rounded text-xs font-bold",
                                parseFloat(usagePct) > 75 ? "bg-red-500/20 text-red-400" :
                                parseFloat(usagePct) > 50 ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"
                              )}>{usagePct}%</span>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StoreReports;
